/**
 * Hook for detecting and respecting reduced motion preferences
 * Supports accessibility requirements for motion-sensitive users
 */

import { useState, useEffect } from 'react';
import { AccessibilityInfo } from 'react-native';

/**
 * Hook that returns true if the user prefers reduced motion
 * Automatically updates when system preferences change
 */
export const useReducedMotion = (): boolean => {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    // Check initial reduced motion preference
    const checkReducedMotion = async () => {
      try {
        const isReduceMotionEnabled =
          await AccessibilityInfo.isReduceMotionEnabled();
        setReduceMotion(isReduceMotionEnabled);
      } catch (error) {
        // Fallback to false if unable to detect
        console.warn('Unable to detect reduced motion preference:', error);
        setReduceMotion(false);
      }
    };

    checkReducedMotion();

    // Listen for changes in reduced motion preference
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      (isReduceMotionEnabled: boolean) => {
        setReduceMotion(isReduceMotionEnabled);
      }
    );

    return () => {
      subscription?.remove();
    };
  }, []);

  return reduceMotion;
};

/**
 * Hook that provides conditional animation values based on reduced motion preference
 */
export const useConditionalAnimation = <T>(
  animatedValue: T,
  staticValue: T
): T => {
  const reduceMotion = useReducedMotion();
  return reduceMotion ? staticValue : animatedValue;
};

/**
 * Hook that provides conditional duration based on reduced motion preference
 */
export const useConditionalDuration = (
  normalDuration: number,
  reducedDuration: number = 0
): number => {
  const reduceMotion = useReducedMotion();
  return reduceMotion ? reducedDuration : normalDuration;
};
